type RelativePath = `/${string}`
type TaskManagerEndpoint = `/workflows${string}`

// --- Composable Types ---
type WithDomain<T = {}> = { domainId: string } & T
type WithWorkflow<T = {}> = { workflowId: string } & T
type WithTurn = { turnId: string }
type WithMessage = { messageId: string }
type WithSchedule = { scheduleId: string }
type WithAgent = { agentId: string }
type WithService = { serviceId: string }
type WithDataAsset = { dataAssetId: string }

// --- Internal Utilities ---
const buildPath = (...parts: string[]): RelativePath =>
  `/${parts.filter(Boolean).join('/')}` as RelativePath

const withPrefix =
  (prefix: string) =>
  (...rest: string[]): RelativePath =>
    buildPath(prefix, ...rest)

// --- USERS API ---

const USERS_BASE = 'api/v0/users'
const users = withPrefix(USERS_BASE)

const usersApi = {
  base: users(),
  requestRuns: users('requestRuns'),
  deactivateAccount: users('deactivateAccount'),
  reactivateAccount: users('reactivateAccount'),
  register: users('register'),
  contact: users('contact'),
  privacyPreferences: users('privacyPreferences'),
  getApiKey: users('getApiKey'),
  disableWelcomeMessage: users('payment', 'disableWelcomeMessage'),
  rateLimit: users('payment', 'rateLimit'),
} satisfies Record<string, RelativePath>

// --- TASK MANAGER API ---

const orchestratorPath = (domainId: string, ...segments: string[]): RelativePath => {
  return `/v0/orchestrators/${domainId}/${segments.join('/')}`
}

const mcpPath = (domainId: string, ...segments: string[]): RelativePath => {
  return `/v0/orchestrators/registry/external/${domainId}/mcp`
}

const mcpServiceIdPath = (domainId: string, ...segments: string[]): RelativePath => {
  return `/v0/orchestrators/registry/external/${domainId}/mcp/${segments.join('/')}`
}

const taskManagerApi = {
  domains: '/v0/orchestrators/domains' as const,

  pauseJourney: ({ journeyViewReference }: { journeyViewReference: string }) =>
    orchestratorPath('em-orchestrator/agent/em-agent-e/tasks', 'pause', journeyViewReference),

  resumeJourney: ({ journeyViewReference }: { journeyViewReference: string }) =>
    orchestratorPath('em-orchestrator/agent/em-agent-e/tasks', 'resume', journeyViewReference),

  agents: ({ domainId }: WithDomain) => orchestratorPath(domainId, 'agents'),

  agentById: ({ domainId, agentId }: WithDomain & WithAgent) =>
    orchestratorPath(domainId, 'agent', agentId),

  workflowsByAgent: ({ domainId, agentId }: WithDomain & WithAgent) =>
    orchestratorPath(domainId, 'workflows', 'agent', agentId),

  workflows: ({ domainId }: WithDomain) => orchestratorPath(domainId, 'workflows'),

  examples: ({ domainId }: WithDomain) => orchestratorPath(domainId, 'workflows/examples'),

  workflow: ({ domainId, workflowId }: WithDomain & WithWorkflow) =>
    orchestratorPath(domainId, 'workflows', workflowId),

  workflowStatus: ({ domainId, workflowId }: WithDomain & WithWorkflow) =>
    orchestratorPath(domainId, 'workflows', workflowId, 'status'),

  workflowTitle: ({ domainId, workflowId }: WithDomain & WithWorkflow) =>
    orchestratorPath(domainId, 'workflows', workflowId, 'title'),

  workflowMessages: ({ domainId, workflowId }: WithDomain & WithWorkflow) =>
    orchestratorPath(domainId, 'workflows', workflowId, 'messages'),

  workflowFeedback: ({ domainId, workflowId }: WithDomain & WithWorkflow) =>
    orchestratorPath(domainId, 'workflows', workflowId, 'feedback'),

  pinWorkflow: ({ domainId, workflowId }: WithDomain & WithWorkflow) =>
    orchestratorPath(domainId, 'workflows', workflowId, 'pin'),

  unpinWorkflow: ({ domainId, workflowId }: WithDomain & WithWorkflow) =>
    orchestratorPath(domainId, 'workflows', workflowId, 'unpin'),

  turnStatus: ({ domainId, workflowId, turnId }: WithDomain & WithWorkflow & WithTurn) =>
    orchestratorPath(domainId, 'workflows', workflowId, 'turns', turnId, 'status'),

  cancelTurn: ({ domainId, workflowId, turnId }: WithDomain & WithWorkflow & WithTurn) =>
    orchestratorPath(domainId, 'workflows', workflowId, 'turns', turnId, 'cancel'),

  messageFeedback: ({ domainId, workflowId, messageId }: WithDomain & WithWorkflow & WithMessage) =>
    orchestratorPath(domainId, 'workflows', workflowId, 'messages', messageId, 'feedback'),

  schedules: ({ domainId }: WithDomain) => orchestratorPath(domainId, 'schedules'),

  schedule: ({ domainId, scheduleId }: WithDomain & WithSchedule) =>
    orchestratorPath(domainId, 'schedules', scheduleId),

  getScheduleWorkflows: ({ domainId, scheduleId }: WithDomain & WithSchedule) =>
    orchestratorPath(domainId, 'schedules', scheduleId, 'runs'),

  pauseSchedule: ({ domainId, scheduleId }: WithDomain & WithSchedule) =>
    orchestratorPath(domainId, 'schedules', scheduleId, 'pause'),

  unpauseSchedule: ({ domainId, scheduleId }: WithDomain & WithSchedule) =>
    orchestratorPath(domainId, 'schedules', scheduleId, 'unpause'),

  mcpAll: ({ domainId }: WithDomain) => mcpPath(domainId),

  mcpCreate: ({ domainId }: WithDomain) => mcpPath(domainId),

  mcp: ({ domainId, serviceId }: WithDomain & WithService) => mcpServiceIdPath(domainId, serviceId),

  mcpUpdate: ({ domainId, serviceId }: WithDomain & WithService) =>
    mcpServiceIdPath(domainId, serviceId),

  mcpDelete: ({ domainId, serviceId }: WithDomain & WithService) =>
    mcpServiceIdPath(domainId, serviceId),

  mcpTools: ({ domainId, serviceId }: WithDomain & WithService) =>
    mcpServiceIdPath(domainId, serviceId, 'tools'),
  dataAssets: ({ domainId }: WithDomain) => orchestratorPath(domainId, 'datastores', 'data-assets'),

  deleteDataAssets: ({ domainId }: WithDomain) =>
    orchestratorPath(domainId, 'datastores', 'data-assets', 'delete'),

  files: ({ domainId }: WithDomain) => orchestratorPath(domainId, 'datastores', 'files'),
} satisfies Record<string, ((props: any) => RelativePath) | RelativePath>

// --- Exports ---

export {
  taskManagerApi as taskManagerApiPaths,
  usersApi as usersApiPaths,
  type RelativePath,
  type TaskManagerEndpoint,
}
